import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BoardJournal } from '../../../stores/boardStore';
import { boardService } from '../../../lib/api/boards';
import { documentAnalysisService, getDocumentAnalysisErrorKey, isDocumentAnalysisError, getErrorCode } from '../../../lib/api/documents';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { useAlertStore } from '../../../stores/alertStore';
import { useAuthStore } from '../../../stores/authStore';
import { ConfirmationModal } from '../../../components/ConfirmationModal';
import { BoardOperationCreationForm } from './BoardOperationCreationForm';
import { SectionLoader } from '../../../components/LogoLoader';
import { AnimatedLogo } from '../../../components/common/AnimatedLogo';
import { ExportOperationsModal } from './modals/ExportOperationsModal';
import { OperationFileExplorerModal } from './modals/OperationFileExplorerModal';
import { usePlanFeaturesStore } from '../../../stores/planFeaturesStore';
import { UpgradeModal } from '../../subscription/UpgradeModal';



interface BoardJournalDetailsProps {
    boardId: string;
    selectedJournal: BoardJournal;
    standalone?: boolean;
    onBack?: () => void;
    readOnly?: boolean;
    sharedBy?: string;
    returnToBoard?: { id: string; name: string } | null;
    onReturnToBoard?: () => void;
    onRename?: () => void;
    minimalMode?: boolean;
    hideBackButton?: boolean;
    hideHeader?: boolean;
    onOpenInFullView?: () => void;
    onOperationCreated?: (opId: string) => void;
    onMenuClick?: () => void;
}

export const BoardJournalDetails: React.FC<BoardJournalDetailsProps> = ({
    boardId,
    selectedJournal,
    standalone = false,
    onBack,
    readOnly = false,
    sharedBy,
    returnToBoard,
    onReturnToBoard,
    onRename,
    minimalMode = false,
    hideBackButton = false,
    hideHeader = false,
    onOpenInFullView,
    onOperationCreated,
    onMenuClick
}) => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { showAlert } = useAlertStore();
    const [fileExplorerOperation, setFileExplorerOperation] = useState<{ id: string, title: string } | null>(null);

    const [editingOperationId, setEditingOperationId] = useState<string | null>(null);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    // AI Processing State
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStep, setProcessingStep] = useState(0);

    // Plan Feature Gate state
    const [upgradeModal, setUpgradeModal] = useState<{ isOpen: boolean; featureKey: string; currentUsage?: number }>({ isOpen: false, featureKey: '' });
    const { getFeatureValue } = usePlanFeaturesStore();

    const processingSteps = [
        'boardJournals.details.aiProcessing.analyzing',
        'boardJournals.details.aiProcessing.extracting',
        'boardJournals.details.aiProcessing.classifying',
        'boardJournals.details.aiProcessing.finalizing'
    ];

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (isProcessing) {
            setProcessingStep(0);
            interval = setInterval(() => {
                setProcessingStep(prev => (prev + 1) % processingSteps.length);
            }, 2500); // Slower transitions so users can read comfortably
        }
        return () => clearInterval(interval);
    }, [isProcessing]);

    const handleCancelProcessing = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsProcessing(false);
    };

    const [isBottomButtonVisible, setIsBottomButtonVisible] = useState(false);
    const bottomButtonRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    const [confirmationModal, setConfirmationModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
    });





    const { data: journalOperations, isLoading } = useQuery({
        queryKey: ['journal-operations', selectedJournal?.id],
        queryFn: () => boardService.getGlobalJournalOperations(selectedJournal.id),
        enabled: !!selectedJournal?.id && selectedJournal.type !== 'system'
    });


    const operations = useMemo(() => {
        if (!journalOperations) return [];

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const getNextOccurrence = (startDate: Date, frequency: string): Date => {
            if (startDate >= today) return startDate;
            const nextDate = new Date(startDate);
            // Safety break to prevent infinite loops with bad data
            let iterations = 0;
            while (nextDate < today && iterations < 1000) {
                if (frequency === 'daily') nextDate.setDate(nextDate.getDate() + 1);
                else if (frequency === 'weekly') nextDate.setDate(nextDate.getDate() + 7);
                else if (frequency === 'monthly') nextDate.setMonth(nextDate.getMonth() + 1);
                else if (frequency === 'yearly') nextDate.setFullYear(nextDate.getFullYear() + 1);
                else return startDate;
                iterations++;
            }
            return nextDate;
        };

        const sortedOperations = [...journalOperations].sort((a: any, b: any) => {
            const dateA = new Date(a.date || a.startDate || 0);
            const dateB = new Date(b.date || b.startDate || 0);
            return dateB.getTime() - dateA.getTime();
        });

        return sortedOperations.map((tOp: any) => {
            const isPeriodic = !!tOp.recurrence || (!!tOp.startDate && !!tOp.frequency);
            const rawDate = tOp.date ? new Date(tOp.date) : (tOp.startDate ? new Date(tOp.startDate) : new Date());

            // Calculate display date (next occurrence for periodic)
            const operationDate = isPeriodic && tOp.frequency
                ? getNextOccurrence(rawDate, tOp.frequency)
                : rawDate;

            const type = tOp.displayType || (tOp.type === 'income' ? 'REVENUE' : 'EXPENSE');

            // Translate frequency if exists
            const frequencyLabel = isPeriodic && tOp.frequency
                ? t(`boardJournals.details.frequencies.${tOp.frequency.toLowerCase()}`, tOp.frequency)
                : t('boardJournals.details.recurring');

            const defaultCurrency = (window as any).__DASHBAL_CONFIG__?.defaultCurrency || 'EUR';
            const currency = tOp.currency || useAuthStore.getState().user?.currency || defaultCurrency;
            if (!currency) {
                console.error(`Operation ${tOp.id} has no currency and no user default found.`);
            }

            let metadata: any = {};
            try {
                metadata = typeof tOp.metadata === 'string' ? JSON.parse(tOp.metadata) : (tOp.metadata || {});
            } catch (e) {
                metadata = {};
            }

            return {
                id: tOp.id,
                title: tOp.description || tOp.category || t('boardJournals.details.untitledOperation'),
                formattedAmount: currency ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: currency
                }).format(tOp.amount || 0) : 'ERR',
                type,
                displayDate: operationDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
                isPeriodic,
                period: isPeriodic ? frequencyLabel : '-',
                hasFile: !!metadata?.fileKey,
                fileName: metadata?.fileName,
                raw: tOp
            };
        });
    }, [journalOperations, t]);

    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isDeleting, setIsDeleting] = useState(false);

    // Reset selection when journal ID changes
    useEffect(() => {
        setSelectedIds(new Set());
        setEditingOperationId(null);
    }, [selectedJournal?.id]);

    const handleToggleSelect = (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) newSet.delete(id);
            else newSet.add(id);
            return newSet;
        });
    };

    // Dropzone configuration
    const onDrop = React.useCallback(async (acceptedFiles: File[]) => {
        if (readOnly || acceptedFiles.length === 0) return;

        // Plan Feature Check: Max Operations per Journal
        const maxOps = getFeatureValue('max_operations_per_journal');
        if (maxOps !== -1 && operations.length >= maxOps) {
            setUpgradeModal({
                isOpen: true,
                featureKey: 'max_operations_per_journal',
                currentUsage: operations.length
            });
            return;
        }

        const file = acceptedFiles[0];
        setIsProcessing(true);

        // Snapshot current op ids so we can identify newly created ones after AI analysis
        const priorOpIds = new Set<string>(
            Array.isArray(journalOperations)
                ? journalOperations.map((op: any) => op.id)
                : []
        );

        try {
            // Get user's default currency
            const defaultCurrency = (window as any).__DASHBAL_CONFIG__?.defaultCurrency || 'EUR';
            const userCurrency = useAuthStore.getState().user?.currency || defaultCurrency;

            // Call the document analysis API and save directly to journal
            const result = await documentAnalysisService.analyzeAndSave(
                file,
                selectedJournal.id,
                { currency: userCurrency }
            );

            // Refresh the operations list
            if (result.savedCount && result.savedCount > 0) {

                // Refresh the operations list
                queryClient.invalidateQueries({
                    queryKey: ['journal-operations', selectedJournal.id]
                });

                // Also invalidate board if we're linked
                const bId = boardId || returnToBoard?.id;
                if (bId) {
                    queryClient.invalidateQueries({ queryKey: ['board-payload', bId] });
                }

                // Auto-select newly created ops on the board (parity with manual create path)
                if (onOperationCreated) {
                    try {
                        const freshOps = await queryClient.fetchQuery({
                            queryKey: ['journal-operations', selectedJournal.id],
                            queryFn: () => boardService.getGlobalJournalOperations(selectedJournal.id),
                            staleTime: 0
                        });
                        if (Array.isArray(freshOps)) {
                            freshOps.forEach((op: any) => {
                                if (op?.id && !priorOpIds.has(op.id)) {
                                    onOperationCreated(op.id);
                                }
                            });
                        }
                    } catch (diffError) {
                        console.error('Failed to diff AI-imported operations for board auto-select', diffError);
                    }
                }

                // Show warning if some operations failed
                if (result.failedCount && result.failedCount > 0) {
                    showAlert({
                        type: 'warning',
                        message: t('documentAnalysis.partialSave', {
                            saved: result.savedCount,
                            total: result.savedCount + result.failedCount
                        })
                    });
                }
            } else {
                // No operations found
                showAlert({
                    type: 'warning',
                    message: t('documentAnalysis.noOperationsFound')
                });
            }

            // Show any warnings from the analysis
            if (result.warnings && result.warnings.length > 0) {
                console.warn('Document analysis warnings:', result.warnings);
            }
        } catch (error: any) {
            console.error('Document analysis failed:', error);

            // Get error message based on error code
            let errorMessage: string;

            if (isDocumentAnalysisError(error)) {
                const errorCode = getErrorCode(error);
                const errorKey = getDocumentAnalysisErrorKey(errorCode || '');
                errorMessage = t(errorKey);
            } else {
                errorMessage = t('documentAnalysis.errors.generic');
            }

            showAlert({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setIsProcessing(false);
        }
    }, [readOnly, selectedJournal.id, boardId, standalone, queryClient, showAlert, t, journalOperations, onOperationCreated, returnToBoard?.id, operations.length, getFeatureValue]);

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        noClick: true, // Prevent clicking container to open file dialog (we have a specific button)
        noKeyboard: true,
        disabled: readOnly,
        onDragEnter: () => !readOnly && setIsDragging(true),
        onDragLeave: () => setIsDragging(false),
        onDropAccepted: () => setIsDragging(false)
    });

    // Update isDragging state based on dropzone state
    useEffect(() => {
        setIsDragging(isDragActive && !readOnly);
    }, [isDragActive, readOnly]);

    const handleSelectAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (selectedIds.size === operations.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(operations.map(((op: any) => op.id))));
        }
    };

    const handleBulkDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (selectedIds.size === 0 || isDeleting) return;

        setConfirmationModal({
            isOpen: true,
            title: t('boardJournals.details.deleteOperations', 'Delete Operations'),
            message: t('boardJournals.details.confirmDeleteBulk', `Are you sure you want to delete ${selectedIds.size} operations?`),
            onConfirm: async () => {
                if (isDeleting) return;
                setIsDeleting(true);
                const idsToDelete = Array.from(selectedIds);
                try {
                    await Promise.allSettled(idsToDelete.map(async (id) => {
                        try {
                            if (standalone) {
                                await boardService.deleteGlobalJournalOperation(selectedJournal.id, id);
                            } else {
                                await boardService.deleteJournalOperation(boardId, selectedJournal.id, id);
                            }
                        } catch (err: any) {
                            if (err?.message?.includes('not found') || err?.response?.status === 404) {
                                return;
                            }
                            throw err;
                        }
                    }));
                    queryClient.invalidateQueries({ queryKey: ['journal-operations', selectedJournal.id] });

                    // Also invalidate board if we're linked
                    const bId = boardId || returnToBoard?.id;
                    if (bId) {
                        queryClient.invalidateQueries({ queryKey: ['board-payload', bId] });
                    }
                    setSelectedIds(new Set());
                } catch (error) {
                    console.error('Failed to bulk delete', error);
                    showAlert({
                        type: 'error',
                        message: t('errors.failedToDeleteOperations', 'Failed to delete operations')
                    });
                } finally {
                    setIsDeleting(false);
                    setConfirmationModal(prev => ({ ...prev, isOpen: false }));
                }
            }
        });
    };

    const handleAddOperation = () => {
        if (readOnly) return;

        // Plan Feature Check: Max Operations per Journal
        const maxOps = getFeatureValue('max_operations_per_journal');
        if (maxOps !== -1 && operations.length >= maxOps) {
            setUpgradeModal({
                isOpen: true,
                featureKey: 'max_operations_per_journal',
                currentUsage: operations.length
            });
            return;
        }

        setIsCreating(true);
    };

    const handleExport = () => {
        setIsExportModalOpen(true);
    };

    useEffect(() => {
        if (isCreating && scrollRef.current) {
            // Wait for creating state to render the form (if conditional) or just scroll
            setTimeout(() => {
                scrollRef.current?.scrollTo({
                    top: scrollRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            }, 100);
        }
    }, [isCreating]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsBottomButtonVisible(entry.isIntersecting);
            },
            { threshold: 0.1 }
        );

        if (bottomButtonRef.current) {
            observer.observe(bottomButtonRef.current);
        }

        return () => observer.disconnect();
    }, [operations.length, isCreating]);

    return (
        <div className="flex flex-col w-full h-full relative">
            <ExportOperationsModal
                isOpen={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                journalId={selectedJournal.id}
                operationIds={selectedIds}
            />
            {/* Header - Outside Card */}
            {!hideHeader && (
            <div className="flex items-center justify-between mb-4 pt-4 lg:pt-0 flex-shrink-0 z-10">
                <div className="flex items-center gap-3">
                    {onMenuClick && (
                        <button
                            onClick={onMenuClick}
                            className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400"
                        >
                            <span className="w-6 h-6" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </button>
                    )}
                    {!hideBackButton && (
                        <button
                            onClick={() => {
                                if (standalone) {
                                    onBack?.();
                                } else {
                                    navigate(`/boards/${boardId}`);
                                }
                            }}
                            className="p-1.5 -ml-1.5 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                            aria-label={t('actions.back') || 'Back'}
                        >
                            <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_bed5f61b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_bed5f61b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </button>
                    )}

                    <div className="flex items-center gap-4">
                        <h2 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 flex items-center gap-3 truncate group">
                            {selectedJournal.name}
                            {!readOnly && onRename && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRename();
                                    }}
                                    className="p-1 -mr-1 text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors ml-1 focus:opacity-100"
                                    title={t('actions.rename') || 'Rename'}
                                >
                                    <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_8d38283b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_8d38283b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                </button>
                            )}
                            {selectedJournal.type === 'system' && (
                                <span className="px-2 py-0.5 md:px-2.5 md:py-1 bg-blue-50 text-blue-700 text-[10px] font-bold rounded-lg border border-blue-100 uppercase tracking-wide flex-shrink-0">
                                    {t('boardJournals.types.automated')}
                                </span>
                            )}
                            {sharedBy && (
                                <span className="px-2 py-0.5 md:px-2.5 md:py-1 bg-accent-primary/5 text-accent-primary text-[11px] font-medium rounded-lg border border-accent-primary/10 flex-shrink-0 flex items-center gap-1">
                                    <span className="w-3 h-3" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_ff1a85d8.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_ff1a85d8.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    {sharedBy}
                                </span>
                            )}
                            {readOnly && (
                                <span className="px-2 py-0.5 md:px-2.5 md:py-1 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-lg border border-amber-100 uppercase tracking-wide flex-shrink-0">
                                    {t('sharing.readOnly', 'Read Only')}
                                </span>
                            )}
                        </h2>
                    </div>
                </div>

                {returnToBoard && (
                    <button
                        onClick={onReturnToBoard}
                        className="flex-shrink-0 px-3 h-7 text-xs font-medium bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-all shadow-sm active:scale-95"
                    >
                        {t('boards.backTo', 'Back to')} {returnToBoard.name}
                    </button>
                )}

                {!returnToBoard && onOpenInFullView && (
                    <button
                        onClick={onOpenInFullView}
                        className="flex-shrink-0 px-3 h-7 text-xs font-medium bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-all shadow-sm active:scale-95"
                    >
                        {t('boardJournals.inBoardModal.openInFullView', 'Open in full view')}
                    </button>
                )}

            </div>
            )}


            {/* Content Card with Drop Zone Support */}
            <div
                {...getRootProps()}
                className={`flex-1 flex flex-col relative overflow-hidden transition-colors duration-200 ${standalone ? 'bg-white dark:bg-neutral-900 rounded-xl border border-neutral-200 dark:border-neutral-800' : 'bg-white dark:bg-neutral-900'} ${isDragging ? 'bg-accent-primary/5 border-accent-primary ring-2 ring-inset ring-accent-primary/20' : ''}`}

            >
                <input {...getInputProps()} />

                {/* AI Processing Overlay */}
                <AnimatePresence>
                    {isProcessing && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 z-50 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-sm flex flex-col items-center justify-center p-6"
                        >
                            <div className="w-full max-w-md flex flex-col items-center text-center">
                                {/* Animated Dashbal Logo */}
                                <div className="relative w-10 h-10 flex items-center justify-center">
                                    <AnimatedLogo variant="processing" size="sm" />
                                </div>

                                {/* Dynamic Text with Animated Dots */}
                                <div className="min-h-6 w-full flex items-center justify-center">
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={processingStep}
                                            initial={{ opacity: 0, y: 5 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -5 }}
                                            transition={{ duration: 0.25 }}
                                            className="w-full flex justify-center" // Ensure width is full and centered
                                        >
                                            <motion.div
                                                animate={{ opacity: [0.5, 1, 0.5] }}
                                                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                                                className="flex items-center justify-center gap-1"
                                            >
                                                <span className="text-sm font-medium text-neutral-700 tracking-tight text-center">
                                                    {t(processingSteps[processingStep])}
                                                </span>
                                                {/* Animated dots replacing ellipsis */}
                                                <span className="flex gap-0.5 ml-0.5">
                                                    {[0, 1, 2].map((i) => (
                                                        <motion.span
                                                            key={i}
                                                            animate={{
                                                                opacity: [0.3, 1, 0.3],
                                                                y: [0, -1.5, 0]
                                                            }}
                                                            transition={{
                                                                duration: 1.2,
                                                                repeat: Infinity,
                                                                delay: i * 0.2,
                                                                ease: "easeInOut"
                                                            }}
                                                            className="w-1 h-1 bg-accent-primary rounded-full"
                                                        />
                                                    ))}
                                                </span>
                                            </motion.div>
                                        </motion.div>
                                    </AnimatePresence>
                                </div>

                                {/* Cancel Link */}
                                <button
                                    onClick={handleCancelProcessing}
                                    className="text-xs text-neutral-400 hover:text-red-500 transition-colors mt-3"
                                >
                                    {t('boardJournals.details.cancelProcessing')}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Drag Overlay */}
                <AnimatePresence>
                    {isDragging && !readOnly && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 z-50 bg-accent-primary/10 backdrop-blur-[2px] flex flex-col items-center justify-center border-2 border-accent-primary border-dashed rounded-lg pointer-events-none"
                        >
                            <div className="bg-white p-4 rounded-full shadow-lg mb-4">
                                <span className="w-8 h-8 text-accent-primary animate-bounce" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_1fcaba95.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_1fcaba95.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                            </div>
                            <h3 className="text-lg font-semibold text-accent-primary text-center">{t('boardJournals.details.dragActive')}</h3>
                            <p className="text-sm text-accent-primary/80 text-center">{t('boardJournals.details.dragActiveSubtitle')}</p>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div ref={scrollRef} className="flex-1 overflow-auto relative scroll-smooth">
                    <div className="w-full pb-6">
                        {/* Unified Table Header - Desktop Only */}
                        <div className="hidden md:grid sticky top-0 z-10 grid-cols-12 gap-4 px-3 py-1.5 text-[11px] font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                            <div className="col-span-3 hidden md:block h-5 flex items-center">{t('boardJournals.details.headers.amount')}</div>
                            <div className="col-span-12 md:col-span-4 h-5 flex items-center">{t('boardJournals.details.headers.operation')}</div>
                            <div className="col-span-3 hidden md:block h-5 flex items-center">{t('boardJournals.details.headers.date')}</div>
                            <div className="col-span-2 hidden md:flex items-center justify-end gap-2 h-5">
                                {selectedIds.size > 0 && !readOnly && !minimalMode && (
                                    <>
                                        <button
                                            onClick={handleExport}
                                            className="h-5 w-5 flex items-center justify-center text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded transition-colors animate-in fade-in"
                                            title={t('actions.export') || "Export"}
                                        >
                                            <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_7a71fb6d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_7a71fb6d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        </button>
                                        <button
                                            onClick={handleBulkDelete}
                                            className="h-5 w-5 flex items-center justify-center text-red-600 hover:bg-red-50 rounded transition-colors animate-in fade-in"
                                            title={t('common.delete') || "Delete"}
                                        >
                                            <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_fdef19e3.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_fdef19e3.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        </button>
                                    </>
                                )}
                                {!readOnly && !minimalMode ? (
                                    <div
                                        onClick={handleSelectAll}
                                        className={`w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-colors ${selectedIds.size > 0 ? 'bg-accent-primary border-accent-primary' : 'bg-white border-neutral-300 hover:border-neutral-400'}`}
                                    >
                                        {selectedIds.size > 0 && (
                                            <span className="w-3 h-3 text-white" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_96d1cb4f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_96d1cb4f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        )}
                                    </div>
                                ) : !minimalMode ? (
                                    <span className="text-right w-full">{t('boardJournals.list.headers.actions')}</span>
                                ) : null}
                            </div>
                        </div>

                        <div className="md:divide-y md:divide-neutral-100 dark:md:divide-neutral-800">
                            {isLoading ? (
                                <div className="p-12 text-center text-neutral-400 flex flex-col items-center">
                                    <SectionLoader size="sm" message={String(t('boardJournals.details.loading'))} />
                                </div>
                            ) : operations.length === 0 && !isCreating ? (
                                <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                                    <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500">

                                        <span className="w-8 h-8" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_a31ca216.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_a31ca216.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    </div>
                                    <h3 className="text-base font-semibold mb-2 text-neutral-900 dark:text-neutral-200">
                                        {(t('boardJournals.details.emptyState'))}
                                    </h3>
                                    <p className="text-sm text-neutral-400 max-w-[200px] mb-6">
                                        {t('boardJournals.details.emptyStateText')}
                                        <button
                                            type="button"
                                            onClick={open}
                                            className="text-accent-primary font-medium mt-3 hover:text-accent-primary/80 hover:underline transition-all cursor-pointer"
                                        >
                                            {t('boardJournals.details.chooseFileOrDragAndDrop', 'Choose a file or drag and drop')}
                                        </button>

                                        {/* Selling Point - AI Analysis */}
                                        <span className="block mt-1.5">
                                            <span className="text-xs font-medium text-accent-primary/80">
                                                {t('boardJournals.details.aiAnalysisPromo', 'for instant AI analysis')}
                                            </span>
                                        </span>
                                    </p>
                                    {!readOnly && (
                                        <div className="flex gap-3 justify-center">
                                            <button
                                                id="onboarding-add-operation"
                                                onClick={handleAddOperation}
                                                className="h-7 px-3 text-xs font-bold bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-all shadow-sm active:scale-95"
                                            >
                                                {t('actions.addOperation')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                                    {operations.map((operation: any) => {
                                        const isEditing = editingOperationId === operation.id;
                                        return (
                                            <React.Fragment key={operation.id}>
                                                <motion.div
                                                    initial={{ opacity: 0, y: 5 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    className={`
                                                    group relative grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 items-center px-3 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors duration-150 cursor-pointer
                                                    ${selectedIds.has(operation.id) ? 'bg-accent-primary/5' : 'bg-white dark:bg-neutral-900'}
                                                    ${isEditing ? 'lg:hidden border-2 border-accent-primary/50 bg-accent-primary/5 rounded-lg z-10 my-1' : ''}
                                                `}
                                                    onClick={() => !readOnly && setEditingOperationId(operation.id)}
                                                >
                                                    {/* Mobile View Refined */}
                                                    <div className="md:hidden flex flex-col w-full gap-2 p-0.5">
                                                        <div className="flex justify-between items-center w-full">
                                                            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-lg ${operation.type === 'EXPENSE' ? 'bg-rose-50/80 text-rose-600 dark:bg-rose-950/20 dark:text-rose-400' : 'bg-emerald-50/80 text-emerald-600 dark:bg-emerald-950/20 dark:text-emerald-400'}`}>
                                                                {operation.type === 'EXPENSE' ? (
                                                                    <span className="w-3 h-3" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_04fd71fb.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_04fd71fb.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                ) : (
                                                                    <span className="w-3 h-3" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_08b79219.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_08b79219.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                )}
                                                                <span className="text-xs font-bold font-mono tracking-tight">{operation.formattedAmount}</span>
                                                            </div>
                                                            <div className="flex items-center gap-1 text-[10px] font-bold text-neutral-400 dark:text-neutral-500 uppercase tracking-wider">
                                                                {operation.isPeriodic && (
                                                                    <span className="w-3 h-3 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_b632366a.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_b632366a.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                )}
                                                                <span>{operation.displayDate}</span>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between gap-3 min-w-0">
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="text-[13px] font-semibold text-neutral-700 dark:text-neutral-200 truncate leading-snug">
                                                                    {operation.title}
                                                                </h4>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Desktop View Content (Existing) */}
                                                    <div className="hidden md:contents flex-1">
                                                        {/* Amount */}
                                                        <div className="md:col-span-3 flex items-center md:justify-start gap-1 md:pl-2 md:order-1 min-w-0">
                                                            <div className={`flex items-center justify-center ${operation.type === 'EXPENSE' ? 'text-rose-500 dark:text-rose-400' : 'text-emerald-500 dark:text-emerald-400'}`}>
                                                                {operation.type === 'EXPENSE' ? (
                                                                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_04fd71fb.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_04fd71fb.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                ) : (
                                                                    <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_08b79219.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_08b79219.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                )}
                                                            </div>
                                                            <div className={`font-medium font-mono text-sm truncate ${operation.type === 'EXPENSE' ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`} title={operation.formattedAmount}>
                                                                {operation.formattedAmount}
                                                            </div>
                                                        </div>

                                                        {/* Title */}
                                                        <div className="mb-2 md:mb-0 md:col-span-4 md:pl-0 md:order-2 min-w-0">
                                                            <div className="font-medium text-neutral-700 dark:text-neutral-300 truncate pr-2 md:pr-0 text-sm" title={operation.title}>
                                                                {operation.title}
                                                            </div>
                                                        </div>

                                                        {/* Desktop Date */}
                                                        <div className="hidden md:flex md:col-span-3 items-center text-sm text-neutral-600 dark:text-neutral-400 md:order-3">
                                                            {operation.isPeriodic && (
                                                                <span className="w-4 h-4 mr-2 text-neutral-400" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_6e41e370.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_6e41e370.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                            )}
                                                            {operation.displayDate}
                                                        </div>

                                                        {/* Actions - Hide if read only */}
                                                        <div className="hidden md:flex md:col-span-2 items-center justify-end relative md:order-4 h-7 gap-3">
                                                             <button
                                                                 onClick={(e) => {
                                                                     e.stopPropagation();
                                                                     setFileExplorerOperation({ id: operation.id, title: operation.title });
                                                                 }}
                                                                 className={`flex items-center gap-1.5 px-2.5 py-1 ${operation.hasFile ? 'text-accent-primary hover:bg-accent-primary/10' : 'text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200'} transition-all rounded-md group/file whitespace-nowrap`}
                                                                 title={operation.fileName || t('common.viewFiles')}
                                                             >
                                                                 <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_79780f52.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_79780f52.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                 <span className="hidden xl:inline text-xs font-semibold">{operation.hasFile ? t('common.viewFiles') : t('common.addFiles')}</span>
                                                             </button>
                                                            {!readOnly && !minimalMode && (
                                                                <>
                                                                    {/* Row Checkbox */}
                                                                    <div
                                                                        onClick={(e) => handleToggleSelect(operation.id, e)}
                                                                        className={`
                                                                        w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-colors flex-shrink-0
                                                                        ${selectedIds.has(operation.id)
                                                                                ? 'bg-accent-primary border-accent-primary'
                                                                                : 'bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 hover:border-neutral-400 dark:hover:border-neutral-500 text-transparent'
                                                                            }
                                                                    `}
                                                                    >
                                                                        <span className="w-3 h-3 text-white" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_25943459.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_25943459.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </motion.div>

                                                {isEditing && (
                                                    <BoardOperationCreationForm
                                                        isOpen={true}
                                                        onClose={() => setEditingOperationId(null)}
                                                        boardId={boardId}
                                                        journalId={selectedJournal.id}
                                                        standalone={standalone}
                                                        operation={operation.raw}
                                                        existingTitles={operations.map((op: any) => op.title)}
                                                        associatedBoardId={returnToBoard?.id}
                                                        onOpenDocuments={() => setFileExplorerOperation({ id: operation.id, title: operation.title })}
                                                    />
                                                )}
                                            </React.Fragment>
                                        );
                                    })}

                                    {/* Inline Creation Form - Placed here for smooth layout transition */}
                                    <BoardOperationCreationForm
                                        isOpen={isCreating}
                                        onClose={() => setIsCreating(false)}
                                        boardId={boardId}
                                        journalId={selectedJournal.id}
                                        standalone={standalone}
                                        existingTitles={operations.map((op: any) => op.title)}
                                        associatedBoardId={returnToBoard?.id}
                                        onOperationCreated={onOperationCreated}
                                    />

                                    {/* Bottom Add Buttons */}
                                    <div ref={bottomButtonRef} className="py-2 px-6 flex flex-col items-center gap-2 overflow-hidden">
                                        <AnimatePresence>
                                            {!isCreating && !editingOperationId && !readOnly && (
                                                <>
                                                    <motion.button
                                                        id="onboarding-add-operation"
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto', transition: { delay: 0.1, duration: 0.2 } }}
                                                        exit={{ opacity: 0, height: 0, transition: { duration: 0.2 } }}
                                                        onClick={handleAddOperation}
                                                        className="flex items-center justify-center w-full py-2 bg-white dark:bg-neutral-900 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg text-neutral-400 dark:text-neutral-500 hover:text-accent-primary hover:border-accent-primary hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors text-xs font-medium gap-2"
                                                    >
                                                        <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_61d25d7c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_61d25d7c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                        {t('actions.addOperation')}
                                                    </motion.button>

                                                    <motion.button
                                                        id="onboarding-upload-file"
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto', transition: { delay: 0.15, duration: 0.2 } }}
                                                        exit={{ opacity: 0, height: 0, transition: { duration: 0.2 } }}
                                                        onClick={open}
                                                        className="flex items-center justify-center w-full py-2 bg-white dark:bg-neutral-900 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-lg text-neutral-400 dark:text-neutral-500 hover:text-accent-primary hover:border-accent-primary hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors text-xs font-medium gap-2"
                                                    >
                                                        <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_70841794.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_70841794.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                                        {t('boardJournals.details.aiFileAnalysis')}
                                                    </motion.button>
                                                </>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Bottom spacer for the floating form - Animate height based on device - Removing since form is now inline on desktop, but might need it for mobile */}
                        {/* <motion.div
                            animate={{ height: isCreating ? (window.innerWidth >= 1024 ? 96 : 280) : 0 }}
                            className="w-full"
                        /> */}
                    </div>
                </div>
            </div>

            {/* Floating Action Button - Only when list is not empty AND bottom button is not visible */}
            {
                operations.length > 0 && !isCreating && !isBottomButtonVisible && !readOnly && (
                    <div className="absolute bottom-6 right-6 z-20 animate-in fade-in zoom-in duration-200 flex items-center gap-3">
                        <button
                            id="onboarding-upload-file"
                            onClick={open}
                            className="w-10 h-10 bg-white dark:bg-neutral-900 text-neutral-400 dark:text-neutral-500 border border-neutral-200 dark:border-neutral-700 rounded-full shadow-sm hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-accent-primary hover:border-accent-primary/30 hover:shadow-md transition-all flex items-center justify-center"
                            aria-label={t('boardJournals.details.aiFileAnalysis')}
                            title={t('boardJournals.details.aiFileAnalysis')}
                        >
                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_cf25ce39.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_cf25ce39.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </button>
                        <button
                            id="onboarding-add-operation"
                            onClick={handleAddOperation}
                            className="w-10 h-10 bg-white text-neutral-400 border border-neutral-200 rounded-full shadow-sm hover:bg-neutral-50 hover:text-accent-primary hover:border-accent-primary/30 hover:shadow-md transition-all flex items-center justify-center"
                            aria-label={t('actions.addOperation')}
                            title={t('actions.addOperation')}
                        >
                            <span className="w-5 h-5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_52aea03c.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_52aea03c.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                        </button>
                    </div>
                )
            }

            {/* Confirmation Modal */}
            <ConfirmationModal
                isOpen={confirmationModal.isOpen}
                title={confirmationModal.title}
                message={confirmationModal.message}
                onConfirm={confirmationModal.onConfirm}
                onCancel={() => setConfirmationModal(prev => ({ ...prev, isOpen: false }))}
                variant="danger"
            />

            <UpgradeModal
                isOpen={upgradeModal.isOpen}
                onClose={() => setUpgradeModal(prev => ({ ...prev, isOpen: false }))}
                featureKey={upgradeModal.featureKey}
                currentUsage={upgradeModal.currentUsage}
            />

            <OperationFileExplorerModal
                isOpen={!!fileExplorerOperation}
                onClose={() => setFileExplorerOperation(null)}
                journalId={selectedJournal.id}
                operationId={fileExplorerOperation?.id || ''}
            />
        </div >
    );
};
